import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { subscriptionsAPI, salesAPI } from '../../services/api';
import { Subscription, SubscriptionStatus, PaymentStatus, PaymentMethod, ProcessPaymentDto } from '../../types';
import SubscriptionPayments from './SubscriptionPayments';
import {
  ArrowLeftIcon,
  PencilIcon,
  TrashIcon,
  CheckCircleIcon,
  XCircleIcon,
  ClockIcon,
  PauseIcon,
  BuildingOfficeIcon,
  CurrencyDollarIcon,
  CalendarIcon,
  UserGroupIcon,
  CreditCardIcon,
  DocumentTextIcon,
  ExclamationTriangleIcon,
  CheckIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';

const SubscriptionDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [subscription, setSubscription] = useState<Subscription | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [processingPayment, setProcessingPayment] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [showRecordPaymentModal, setShowRecordPaymentModal] = useState(false);
  const [paymentData, setPaymentData] = useState<ProcessPaymentDto>({
    paymentMethod: PaymentMethod.CASH,
    receiptNumber: '',
    notes: '',
  });
  const [paymentsRefreshKey, setPaymentsRefreshKey] = useState<number>(0);

  useEffect(() => {
    if (!id) return;
    (async () => {
      await loadSubscription();
      setPaymentsRefreshKey((k) => k + 1);
    })();
  }, [id]);

  const loadSubscription = async () => {
    if (!id) return;

    try {
      setLoading(true);
      setError(null);
      const subscriptionData = await subscriptionsAPI.getById(id);
      setSubscription(subscriptionData);
    } catch (err) {
      setError('Failed to load subscription');
      console.error('Error loading subscription:', err);
    } finally {
      setLoading(false);
    }
  };

  const getStatusIcon = (status: SubscriptionStatus) => {
    switch (status) {
      case SubscriptionStatus.ACTIVE:
        return <CheckCircleIcon className="h-5 w-5 text-green-500" />;
      case SubscriptionStatus.EXPIRED:
        return <XCircleIcon className="h-5 w-5 text-red-500" />;
      case SubscriptionStatus.CANCELLED:
        return <XCircleIcon className="h-5 w-5 text-gray-500" />;
      case SubscriptionStatus.PENDING:
        return <ClockIcon className="h-5 w-5 text-yellow-500" />;
      case SubscriptionStatus.SUSPENDED:
        return <PauseIcon className="h-5 w-5 text-orange-500" />;
      default:
        return <ClockIcon className="h-5 w-5 text-gray-500" />;
    }
  };

  const getStatusBadge = (status: SubscriptionStatus) => {
    const baseClasses = "inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium";
    switch (status) {
      case SubscriptionStatus.ACTIVE:
        return `${baseClasses} bg-green-100 text-green-800`;
      case SubscriptionStatus.EXPIRED:
        return `${baseClasses} bg-red-100 text-red-800`;
      case SubscriptionStatus.CANCELLED:
        return `${baseClasses} bg-gray-100 text-gray-800`;
      case SubscriptionStatus.PENDING:
        return `${baseClasses} bg-yellow-100 text-yellow-800`;
      case SubscriptionStatus.SUSPENDED:
        return `${baseClasses} bg-orange-100 text-orange-800`;
      default:
        return `${baseClasses} bg-gray-100 text-gray-800`;
    }
  };

  const getPaymentStatusBadge = (status: PaymentStatus) => {
    const baseClasses = "inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium";
    switch (status) {
      case PaymentStatus.PAID:
        return `${baseClasses} bg-green-100 text-green-800`;
      case PaymentStatus.PENDING:
        return `${baseClasses} bg-yellow-100 text-yellow-800`;
      case PaymentStatus.FAILED:
        return `${baseClasses} bg-red-100 text-red-800`;
      case PaymentStatus.REFUNDED:
        return `${baseClasses} bg-blue-100 text-blue-800`;
      default:
        return `${baseClasses} bg-gray-100 text-gray-800`;
    }
  };

  const formatCurrency = (amount: number) => {
    const code = subscription?.currency?.code || 'USD';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: code,
    }).format(amount);
  };

  const formatDate = (date: Date) => {
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const formatDateTime = (date: Date) => {
    return new Date(date).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const isExpired = (endDate: Date) => {
    return new Date(endDate) < new Date();
  };

  const isExpiringSoon = (endDate: Date) => {
    const thirtyDaysFromNow = new Date();
    thirtyDaysFromNow.setDate(thirtyDaysFromNow.getDate() + 30);
    return new Date(endDate) <= thirtyDaysFromNow && new Date(endDate) > new Date();
  };

  const handleProcessPayment = async () => {
    if (!subscription) return;

    try {
      setProcessingPayment(true);
      await subscriptionsAPI.processPayment(subscription.id, paymentData);
      setShowPaymentModal(false);
      setPaymentData({
        paymentMethod: PaymentMethod.CASH,
        receiptNumber: '',
        notes: '',
      });
      loadSubscription(); // Reload to get updated data
    } catch (err) {
      setError('Failed to process payment');
      console.error('Error processing payment:', err);
    } finally {
      setProcessingPayment(false);
    }
  };

  const handleRecordPayment = async () => {
    if (!subscription) return;
    try {
      setProcessingPayment(true);
      await salesAPI.recordSubscriptionPayment(subscription.id, paymentData);
      setShowRecordPaymentModal(false);
      setPaymentData({
        paymentMethod: PaymentMethod.CASH,
        receiptNumber: '',
        notes: '',
      });
      await loadSubscription();
      setPaymentsRefreshKey((k) => k + 1);
    } catch (err) {
      setError('Failed to record payment');
      console.error('Error recording payment:', err);
    } finally {
      setProcessingPayment(false);
    }
  };

  const handleCancelSubscription = async () => {
    if (!subscription || !window.confirm('Are you sure you want to cancel this subscription?')) {
      return;
    }

    try {
      await subscriptionsAPI.cancelSubscription(subscription.id, 'Cancelled by admin');
      loadSubscription();
    } catch (err) {
      setError('Failed to cancel subscription');
      console.error('Error cancelling subscription:', err);
    }
  };

  const handleSuspendSubscription = async () => {
    if (!subscription || !window.confirm('Are you sure you want to suspend this subscription?')) {
      return;
    }

    try {
      await subscriptionsAPI.suspendSubscription(subscription.id, 'Suspended by admin');
      loadSubscription();
    } catch (err) {
      setError('Failed to suspend subscription');
      console.error('Error suspending subscription:', err);
    }
  };

  const handleDelete = async () => {
    if (!subscription || !window.confirm('Are you sure you want to delete this subscription?')) {
      return;
    }

    try {
      await subscriptionsAPI.delete(subscription.id);
      window.location.href = '/subscriptions';
    } catch (err) {
      setError('Failed to delete subscription');
      console.error('Error deleting subscription:', err);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (!subscription) {
    return (
      <div className="text-center py-12">
        <h3 className="mt-2 text-sm font-medium text-gray-900">Subscription not found</h3>
        <p className="mt-1 text-sm text-gray-500">The subscription you're looking for doesn't exist.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <Link
            to="/subscriptions"
            className="p-2 text-gray-400 hover:text-gray-600 hover:bg-white rounded-lg transition-all duration-200"
          >
            <ArrowLeftIcon className="h-5 w-5" />
          </Link>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Subscription Details</h1>
            <p className="text-gray-600">Manage subscription and billing information</p>
          </div>
        </div>
        <div className="flex items-center space-x-2">
          <Link
            to={`/subscriptions/${subscription.id}/edit`}
            className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
          >
            <PencilIcon className="h-4 w-4 mr-2" />
            Edit
          </Link>
          <button
            onClick={handleDelete}
            className="inline-flex items-center px-4 py-2 border border-red-300 rounded-md text-sm font-medium text-red-700 hover:bg-red-50"
          >
            <TrashIcon className="h-4 w-4 mr-2" />
            Delete
          </button>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-md p-4">
          <div className="text-red-800">{error}</div>
        </div>
      )}

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        {/* Subscription Information */}
        <div className="lg:col-span-2 space-y-6">
          {/* Company Information */}
          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
              <BuildingOfficeIcon className="h-5 w-5 mr-2" />
              Company Information
            </h3>
            
            <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
              <div>
                <span className="text-sm font-medium text-gray-700">Company Name</span>
                <div className="text-sm text-gray-900">{subscription.company?.name}</div>
              </div>
              <div>
                <span className="text-sm font-medium text-gray-700">Company Email</span>
                <div className="text-sm text-gray-900">{subscription.company?.email}</div>
              </div>
              <div>
                <span className="text-sm font-medium text-gray-700">Company Phone</span>
                <div className="text-sm text-gray-900">{subscription.company?.phone}</div>
              </div>
              <div>
                <span className="text-sm font-medium text-gray-700">Location</span>
                <div className="text-sm text-gray-900">
                  {subscription.company?.city && subscription.company?.state 
                    ? `${subscription.company.city}, ${subscription.company.state}`
                    : 'Not specified'
                  }
                </div>
              </div>
              <div>
                <span className="text-sm font-medium text-gray-700">Currency</span>
                <div className="text-sm text-gray-900">
                  {subscription.currency?.code || 'USD'}{subscription.currency?.symbol ? ` (${subscription.currency.symbol})` : ''}
                </div>
              </div>
            </div>
          </div>

          {/* Subscription Details */}
          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
              <CreditCardIcon className="h-5 w-5 mr-2" />
              Subscription Details
            </h3>
            
            <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
              <div>
                <span className="text-sm font-medium text-gray-700">Status</span>
                <div className="flex items-center mt-1">
                  {getStatusIcon(subscription.status)}
                  <span className={`ml-2 ${getStatusBadge(subscription.status)}`}>
                    {subscription.status}
                  </span>
                  {isExpired(subscription.endDate) && (
                    <span className="ml-2 text-xs text-red-600">Expired</span>
                  )}
                  {isExpiringSoon(subscription.endDate) && (
                    <span className="ml-2 text-xs text-yellow-600">Expiring Soon</span>
                  )}
                </div>
              </div>
              <div>
                <span className="text-sm font-medium text-gray-700">Payment Status</span>
                <div className="mt-1">
                  <span className={getPaymentStatusBadge(subscription.paymentStatus)}>
                    {subscription.paymentStatus}
                  </span>
                </div>
              </div>
              <div>
                <span className="text-sm font-medium text-gray-700">Invoice Number</span>
                <div className="text-sm text-gray-900">
                  {subscription.invoiceNumber || 'Not generated'}
                </div>
              </div>
              <div>
                <span className="text-sm font-medium text-gray-700">Receipt Number</span>
                <div className="text-sm text-gray-900">
                  {subscription.receiptNumber || 'Not provided'}
                </div>
              </div>
            </div>
          </div>

          {/* Financial Information */}
          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
              <CurrencyDollarIcon className="h-5 w-5 mr-2" />
              Financial Information
            </h3>
            
            <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
              <div>
                <span className="text-sm font-medium text-gray-700">Base Price</span>
                <div className="text-lg font-semibold text-gray-900">
                  {formatCurrency(subscription.basePrice)}
                </div>
              </div>
              <div>
                <span className="text-sm font-medium text-gray-700">Price Per Location</span>
                <div className="text-lg font-semibold text-gray-900">
                  {formatCurrency(subscription.pricePerLocation)}
                </div>
              </div>
              <div>
                <span className="text-sm font-medium text-gray-700">Total Amount</span>
                <div className="text-lg font-semibold text-green-600">
                  {formatCurrency(subscription.totalAmount)}
                </div>
              </div>
              <div>
                <span className="text-sm font-medium text-gray-700">Active Locations</span>
                <div className="text-lg font-semibold text-gray-900">
                  {subscription.activeLocations}
                </div>
              </div>
              <div>
                <span className="text-sm font-medium text-gray-700">Payment Method</span>
                <div className="text-sm text-gray-900">
                  {subscription.paymentMethod}
                </div>
              </div>
              <div>
                <span className="text-sm font-medium text-gray-700">Paid At</span>
                <div className="text-sm text-gray-900">
                  {subscription.paidAt ? formatDateTime(subscription.paidAt) : 'Not paid yet'}
                </div>
              </div>
            </div>
          </div>

          {/* Subscription Period */}
          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
              <CalendarIcon className="h-5 w-5 mr-2" />
              Subscription Period
            </h3>
            
            <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
              <div>
                <span className="text-sm font-medium text-gray-700">Start Date</span>
                <div className="text-sm text-gray-900">{formatDate(subscription.startDate)}</div>
              </div>
              <div>
                <span className="text-sm font-medium text-gray-700">End Date</span>
                <div className="text-sm text-gray-900">{formatDate(subscription.endDate)}</div>
              </div>
              <div>
                <span className="text-sm font-medium text-gray-700">Created At</span>
                <div className="text-sm text-gray-900">{formatDateTime(subscription.createdAt)}</div>
              </div>
              <div>
                <span className="text-sm font-medium text-gray-700">Last Updated</span>
                <div className="text-sm text-gray-900">{formatDateTime(subscription.updatedAt)}</div>
              </div>
            </div>
          </div>

          {/* Notes */}
          {(subscription.notes || subscription.adminNotes) && (
            <div className="bg-white rounded-lg shadow p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
                <DocumentTextIcon className="h-5 w-5 mr-2" />
                Notes
              </h3>
              
              <div className="space-y-4">
                {subscription.notes && (
                  <div>
                    <span className="text-sm font-medium text-gray-700">Company Notes</span>
                    <p className="mt-1 text-sm text-gray-900 whitespace-pre-wrap">{subscription.notes}</p>
                  </div>
                )}
                {subscription.adminNotes && (
                  <div>
                    <span className="text-sm font-medium text-gray-700">Admin Notes</span>
                    <p className="mt-1 text-sm text-gray-900 whitespace-pre-wrap">{subscription.adminNotes}</p>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Quick Actions */}
          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Quick Actions</h3>
            
            <div className="space-y-3">
              {subscription.paymentStatus === PaymentStatus.PENDING && (
                <button
                  onClick={() => setShowPaymentModal(true)}
                  className="w-full inline-flex items-center justify-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-green-600 hover:bg-green-700"
                >
                  <CheckIcon className="h-4 w-4 mr-2" />
                  Process Payment
                </button>
              )}
              
              <button
                onClick={() => setShowRecordPaymentModal(true)}
                className="w-full inline-flex items-center justify-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
              >
                <CreditCardIcon className="h-4 w-4 mr-2" />
                Record a Payment
              </button>
              
              {subscription.status === SubscriptionStatus.ACTIVE && (
                <button
                  onClick={handleSuspendSubscription}
                  className="w-full inline-flex items-center justify-center px-4 py-2 border border-orange-300 text-sm font-medium rounded-md text-orange-700 bg-orange-50 hover:bg-orange-100"
                >
                  <PauseIcon className="h-4 w-4 mr-2" />
                  Suspend Subscription
                </button>
              )}
              
              {subscription.status === SubscriptionStatus.SUSPENDED && (
                <button
                  onClick={() => window.location.href = `/subscriptions/${subscription.id}/edit`}
                  className="w-full inline-flex items-center justify-center px-4 py-2 border border-green-300 text-sm font-medium rounded-md text-green-700 bg-green-50 hover:bg-green-100"
                >
                  <CheckCircleIcon className="h-4 w-4 mr-2" />
                  Reactivate Subscription
                </button>
              )}
              
              {subscription.status !== SubscriptionStatus.CANCELLED && (
                <button
                  onClick={handleCancelSubscription}
                  className="w-full inline-flex items-center justify-center px-4 py-2 border border-red-300 text-sm font-medium rounded-md text-red-700 bg-red-50 hover:bg-red-100"
                >
                  <XMarkIcon className="h-4 w-4 mr-2" />
                  Cancel Subscription
                </button>
              )}
            </div>
          </div>

          {/* Status Alerts */}
          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Status Alerts</h3>
            
            <div className="space-y-3">
              {isExpired(subscription.endDate) && (
                <div className="flex items-center p-3 bg-red-50 border border-red-200 rounded-lg">
                  <ExclamationTriangleIcon className="h-5 w-5 text-red-500 mr-2" />
                  <div className="text-sm text-red-800">
                    <div className="font-medium">Subscription Expired</div>
                    <div>Ended on {formatDate(subscription.endDate)}</div>
                  </div>
                </div>
              )}
              
              {isExpiringSoon(subscription.endDate) && (
                <div className="flex items-center p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                  <ClockIcon className="h-5 w-5 text-yellow-500 mr-2" />
                  <div className="text-sm text-yellow-800">
                    <div className="font-medium">Expiring Soon</div>
                    <div>Expires on {formatDate(subscription.endDate)}</div>
                  </div>
                </div>
              )}
              
              {subscription.paymentStatus === PaymentStatus.PENDING && (
                <div className="flex items-center p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                  <CurrencyDollarIcon className="h-5 w-5 text-yellow-500 mr-2" />
                  <div className="text-sm text-yellow-800">
                    <div className="font-medium">Payment Pending</div>
                    <div>Amount: {formatCurrency(subscription.totalAmount)}</div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Subscription Payments */}
      <SubscriptionPayments subscriptionId={subscription.id} refreshKey={paymentsRefreshKey} />

      {/* Payment Modal */}
      {showPaymentModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Process Payment</h3>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Payment Method</label>
                  <select
                    value={paymentData.paymentMethod}
                    onChange={(e) => setPaymentData(prev => ({ ...prev, paymentMethod: e.target.value as PaymentMethod }))}
                    className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500"
                  >
                    <option value={PaymentMethod.CASH}>Cash</option>
                  </select>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700">Receipt Number</label>
                  <input
                    type="text"
                    value={paymentData.receiptNumber}
                    onChange={(e) => setPaymentData(prev => ({ ...prev, receiptNumber: e.target.value }))}
                    className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500"
                    placeholder="Optional"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700">Notes</label>
                  <textarea
                    value={paymentData.notes}
                    onChange={(e) => setPaymentData(prev => ({ ...prev, notes: e.target.value }))}
                    rows={3}
                    className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500"
                    placeholder="Optional payment notes..."
                  />
                </div>
              </div>
              
              <div className="flex justify-end space-x-3 mt-6">
                <button
                  onClick={() => setShowPaymentModal(false)}
                  className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleProcessPayment}
                  disabled={processingPayment}
                  className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 disabled:opacity-50"
                >
                  {processingPayment ? 'Processing...' : 'Process Payment'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Record Payment Modal (creates Sale) */}
      {showRecordPaymentModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Record a Payment</h3>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Payment Method</label>
                  <select
                    value={paymentData.paymentMethod}
                    onChange={(e) => setPaymentData(prev => ({ ...prev, paymentMethod: e.target.value as PaymentMethod }))}
                    className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500"
                  >
                    <option value={PaymentMethod.CASH}>Cash</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Receipt Number</label>
                  <input
                    type="text"
                    value={paymentData.receiptNumber}
                    onChange={(e) => setPaymentData(prev => ({ ...prev, receiptNumber: e.target.value }))}
                    className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500"
                    placeholder="Optional"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Notes</label>
                  <textarea
                    value={paymentData.notes}
                    onChange={(e) => setPaymentData(prev => ({ ...prev, notes: e.target.value }))}
                    rows={3}
                    className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500"
                    placeholder="Optional payment notes..."
                  />
                </div>
              </div>
              <div className="flex justify-end space-x-3 mt-6">
                <button
                  onClick={() => setShowRecordPaymentModal(false)}
                  className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleRecordPayment}
                  disabled={processingPayment}
                  className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50"
                >
                  {processingPayment ? 'Recording...' : 'Record Payment'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SubscriptionDetail;
